/**
 * PUNISH'D THEME - GLOBAL JAVASCRIPT
 * General theme functionality and utilities
 */

(function() {
  'use strict';

  /**
   * Debounce function
   */
  function debounce(fn, wait) {
    let t;
    return function() {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  /**
   * Fetch API helper
   */
  async function fetchJSON(url, options = {}) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  }

  /**
   * Product Image Gallery
   */
  class ProductGallery {
    constructor() {
      this.gallery = document.querySelector('[data-product-gallery]');
      if (!this.gallery) return;

      this.mainImage = this.gallery.querySelector('#ProductMainImage');
      this.thumbnails = this.gallery.querySelectorAll('[data-media-id]');

      this.init();
    }

    init() {
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.updateMainImage(thumb);
        });
      });
    }

    updateMainImage(thumb) {
      // Remove active class from all thumbnails
      this.thumbnails.forEach(t => t.classList.remove('active'));

      // Add active class to clicked thumbnail
      thumb.classList.add('active');

      // Update main image
      const img = thumb.querySelector('img');
      if (img && this.mainImage) {
        this.mainImage.src = img.src.replace('_150x', '_800x');
        this.mainImage.srcset = img.srcset.replace(/150/g, '800');
      }
    }
  }

  /**
   * Mobile Menu Toggle
   */
  class MobileMenu {
    constructor() {
      this.menuToggle = document.querySelector('[data-mobile-menu-toggle]');
      this.menu = document.querySelector('[data-mobile-menu]');

      if (!this.menuToggle || !this.menu) return;

      this.init();
    }

    init() {
      this.menuToggle.addEventListener('click', () => {
        this.menu.classList.toggle('active');
        this.menuToggle.setAttribute('aria-expanded',
          this.menu.classList.contains('active'));
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.menu.classList.contains('active')) {
          this.menu.classList.remove('active');
          this.menuToggle.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  /**
   * Search Toggle
   */
  class SearchToggle {
    constructor() {
      this.searchToggle = document.querySelector('[data-search-toggle]');
      this.searchDrawer = document.querySelector('[data-search-drawer]');

      if (!this.searchToggle) return;

      this.init();
    }

    init() {
      this.searchToggle.addEventListener('click', () => {
        if (this.searchDrawer) {
          this.searchDrawer.classList.toggle('active');
        }
      });
    }
  }

  /**
   * Quick View Modal
   */
  class QuickView {
    constructor() {
      this.modal = null;
      this.init();
    }

    init() {
      document.addEventListener('click', async (e) => {
        const quickViewBtn = e.target.closest('[data-quickview]');
        if (!quickViewBtn) return;

        e.preventDefault();
        const productId = quickViewBtn.dataset.quickview;
        await this.show(productId);
      });
    }

    async show(productId) {
      try {
        // Fetch product data
        const product = await fetchJSON(`/products/${productId}.js`);

        // Create and show modal (simplified version)
        this.modal = document.createElement('div');
        this.modal.className = 'quick-view-modal';
        this.modal.innerHTML = `
          <div class="quick-view-overlay"></div>
          <div class="quick-view-content">
            <button class="quick-view-close">&times;</button>
            <h2>${product.title}</h2>
            <p>${product.price / 100}</p>
            <!-- Add more product details here -->
          </div>
        `;

        document.body.appendChild(this.modal);

        // Close handlers
        this.modal.querySelector('.quick-view-close').addEventListener('click', () => {
          this.close();
        });

        this.modal.querySelector('.quick-view-overlay').addEventListener('click', () => {
          this.close();
        });
      } catch (error) {
        console.error('Error loading quick view:', error);
      }
    }

    close() {
      if (this.modal) {
        this.modal.remove();
        this.modal = null;
      }
    }
  }

  /**
   * Share Button
   */
  class ShareButton {
    constructor() {
      this.buttons = document.querySelectorAll('[data-share-button]');
      this.init();
    }

    init() {
      this.buttons.forEach(button => {
        button.addEventListener('click', () => {
          if (navigator.share) {
            navigator.share({
              title: document.title,
              url: window.location.href
            }).catch(err => console.log('Error sharing:', err));
          } else {
            // Fallback: copy to clipboard
            this.copyToClipboard(window.location.href);
            button.textContent = 'Link copied!';
            setTimeout(() => {
              button.textContent = 'Share this pun';
            }, 2000);
          }
        });
      });
    }

    copyToClipboard(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  /**
   * Lazy Loading Images
   */
  function initLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
            }
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
            }
            img.classList.remove('lazy');
            observer.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img.lazy').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  /**
   * Accessibility: Trap Focus in Modals
   */
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])'
    );

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    element.addEventListener('keydown', function(e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    });
  }

  /**
   * Initialize everything on DOM ready
   */
  document.addEventListener('DOMContentLoaded', () => {
    new ProductGallery();
    new MobileMenu();
    new SearchToggle();
    new QuickView();
    new ShareButton();
    initLazyLoading();

    // Expose utilities globally
    window.PunishdTheme = {
      debounce,
      fetchJSON,
      trapFocus
    };
  });

  /**
   * Announce dynamic page updates to screen readers
   */
  function announce(message, priority = 'polite') {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.classList.add('visually-hidden');
    document.body.appendChild(announcer);

    setTimeout(() => {
      announcer.textContent = message;
    }, 100);

    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  }

  window.announce = announce;

})();
